/*
 * 🚀 MODERN READING SCREEN - Admin-Frontoffice Compatible
 *
 * This ReadingScreen is fully compatible with the admin WYSIWYG editor:
 * - All page element types (text, images, bubbles, shapes, backgrounds, drop-zones, word-guess)
 * - Full animation support (move-to, fade, bounce, etc.)
 * - Interactive elements (onClick, onHover, onLoad events)
 * - Actions (playSound, visualEffect, showMessage, navigate, grantReward)
 * - Rich text with Lexical
 * - Audio (background music + sound effects)
 * - Multi-language support
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, X, Minimize } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { GameState, Book, StoryProgress } from '../types';
import { getPageDimensions } from '../constants/pageConstants';
import { getPublicStorageUrl } from '../utils/storage';
import { lexicalToHtml } from '../utils/lexicalToHtml';
import { generateCustomKeyframes } from '../utils/animationRenderer';
import ParticleCanvas from './ParticleCanvas';
import AutoTextCarousel, { AutoTextCarouselRef } from './AutoTextCarousel';
import { useLanguageContext } from '../contexts/LanguageContext';
import { useMotionPermission } from '../contexts/MotionPermissionContext';

interface ModernReadingScreenProps {
  gameState: GameState;
  selectedBook: Book;
  onBack: () => void;
  onPageChange: (pageKey: string) => void;
  onBookComplete: (book: Book, visitedRewardIds: number[]) => void;
  onSaveProgress: (progress: StoryProgress) => void;
  onClearProgress: (storyId: number) => void;
}

type Language = 'fr' | 'en' | 'es' | 'de' | 'it' | 'pt' | 'nl';

const ModernReadingScreen: React.FC<ModernReadingScreenProps> = ({
  gameState,
  selectedBook,
  onBack,
  onPageChange,
  onBookComplete,
  onSaveProgress,
  onClearProgress
}) => {
  const { language } = useLanguageContext();
  const startPage = selectedBook.fullData?.startPage || 'start';

  // Check if this is a replay (book already completed)
  const isReplay = gameState.completedBooks.includes(selectedBook.id);

  // Check for saved progress (only for first-time reading)
  const savedProgress = !isReplay && gameState.storyProgress?.storyId === selectedBook.id
    ? gameState.storyProgress
    : null;

  const initialPage = savedProgress?.currentPageKey || startPage;
  const initialHistory = savedProgress?.pageHistory || [startPage];
  const initialAllVisited = savedProgress?.allVisitedPages || [startPage];

  const [currentPageKey, setCurrentPageKey] = useState(initialPage);
  const [showChoices, setShowChoices] = useState(false);
  const [pageHistory, setPageHistory] = useState<string[]>(initialHistory);
  const [allVisitedPages, setAllVisitedPages] = useState<string[]>(initialAllVisited);
  const [windowSize, setWindowSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  const [isMuted, setIsMuted] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [activeEffects, setActiveEffects] = useState<{ [elementId: string]: string }>({});
  const [draggedElement, setDraggedElement] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [elementPositions, setElementPositions] = useState<{ [elementId: string]: { x: number; y: number } }>({});
  const [hasDragged, setHasDragged] = useState(false);
  const [pageReady, setPageReady] = useState(false);

  // Préchargement de toutes les images avant affichage
  const preloadPageImages = useCallback(async (pageElements: any[]) => {
    if (!pageElements || pageElements.length === 0) {
      setPageReady(true);
      return;
    }

    const images = pageElements.filter((el: any) => el.type === 'image' && el.src && el.visible !== false);

    if (images.length === 0) {
      setPageReady(true);
      return;
    }

    const preloadImage = (src: string): Promise<void> => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = getPublicStorageUrl(src);
      });
    };

    // Charger toutes les images en parallèle avec un timeout de sécurité
    await Promise.race([
      Promise.all(images.map((img: any) => preloadImage(img.src))),
      new Promise(resolve => setTimeout(resolve, 5000)) // max 5s d'attente
    ]);

    setPageReady(true);
  }, []);

  // Pinch-to-zoom state
  const [userZoom, setUserZoom] = useState(1);
  const [zoomOffset, setZoomOffset] = useState({ x: 0, y: 0 });
  const initialPinchDistance = useRef<number | null>(null);
  const initialZoom = useRef<number>(1);
  const zoomContainerRef = useRef<HTMLDivElement>(null);
  const initialScrollPos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const pinchCenter = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Reset zoom when entering/re-entering reading screen or changing book
  useEffect(() => {
    setUserZoom(1);
    setZoomOffset({ x: 0, y: 0 });
    if (zoomContainerRef.current) {
      zoomContainerRef.current.scrollLeft = 0;
      zoomContainerRef.current.scrollTop = 0;
    }
  }, [selectedBook.id]);

  // Parallax effect state
  const { motionPermission, motionEnabled } = useMotionPermission();
  const [parallaxOffset, setParallaxOffset] = useState({ x: 0, y: 0 });
  const isNative = Capacitor.isNativePlatform();

  // Parallax multipliers by layer
  // Négatif = bouge dans le sens opposé au gyro (arrière-plan, effet fenêtre)
  // Positif = bouge dans le même sens (premier plan, suit le regard)
  const getParallaxMultiplier = useCallback((layer: string | undefined): number => {
    switch (layer) {
      case 'background': return -0.015; // Opposé au mouvement (loin)
      case 'midground': return 0;       // Neutre (point de référence)
      case 'foreground': return 0.02;   // Suit le mouvement (proche)
      default: return 0; // Default neutre
    }
  }, []);

  // Listen to device orientation (mobile gyroscope)
  // Note: App forces landscape mode, so we swap beta/gamma axes
  useEffect(() => {
    if (!isNative || !motionEnabled || motionPermission === 'denied') return;

    const handleOrientation = (event: DeviceOrientationEvent) => {
      const beta = event.beta || 0;  // Front-back tilt (-180 to 180)
      const gamma = event.gamma || 0; // Left-right tilt (-90 to 90)

      // En mode paysage: beta contrôle X, gamma contrôle Y
      // Position neutre: beta ≈ 0, gamma ≈ -30
      const normalizedX = Math.max(-1, Math.min(1, beta / 30));
      const normalizedY = Math.max(-1, Math.min(1, -(gamma + 30) / 30));

      setParallaxOffset({ x: normalizedX, y: normalizedY });
    };

    window.addEventListener('deviceorientation', handleOrientation);
    return () => window.removeEventListener('deviceorientation', handleOrientation);
  }, [isNative, motionEnabled, motionPermission]);

  // Listen to mouse movement (web)
  useEffect(() => {
    if (isNative) return; // Only for web

    const handleMouseMove = (event: MouseEvent) => {
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;

      // Normalize to -1 to 1 range from center of screen
      const normalizedX = (event.clientX - centerX) / centerX;
      const normalizedY = (event.clientY - centerY) / centerY;

      setParallaxOffset({ x: normalizedX, y: normalizedY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isNative]);

  const contentRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const eventAudioRefs = useRef<{ [key: string]: HTMLAudioElement }>({});
  // Debounce pour éviter double-appels sur éléments draggables
  const lastSoundActionTime = useRef<{ [key: string]: number }>({});
  const autoTextCarouselRefs = useRef<{ [elementId: string]: AutoTextCarouselRef | null }>({});

  // Stop all event sounds
  const stopAllEventSounds = () => {
    Object.values(eventAudioRefs.current).forEach(audio => {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (_) {}
    });
    eventAudioRefs.current = {};
  };

  // Handle back with sound cleanup
  const handleBack = () => {
    stopAllEventSounds();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    onBack();
  };

  const storyPages = selectedBook.fullData?.pages || {};
  const currentPage = storyPages[currentPageKey];
  const storyOrientation = selectedBook.fullData?.orientation;
  const effectiveOrientation = currentPage?.orientation || storyOrientation || 'landscape';
  const PAGE_DIMENSIONS = getPageDimensions(effectiveOrientation);

  // Précharger les images quand la page change
  useEffect(() => {
    setPageReady(false);
    if (currentPage?.content?.elements) {
      preloadPageImages(currentPage.content.elements);
    } else {
      setPageReady(true);
    }
  }, [currentPageKey, currentPage?.content?.elements, preloadPageImages]);

  // Get screen background color from page property
  const pageBackgroundColor = currentPage?.screenBackgroundColor || '#f5f5f5';

  // Determine if background is dark to adjust control colors
  const isBackgroundDark = useMemo(() => {
    const color = pageBackgroundColor;
    let r = 0, g = 0, b = 0;
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else if (hex.length === 6) {
        r = parseInt(hex.slice(0, 2), 16);
        g = parseInt(hex.slice(2, 4), 16);
        b = parseInt(hex.slice(4, 6), 16);
      }
    } else if (color.startsWith('rgb')) {
      const match = color.match(/\d+/g);
      if (match) {
        r = parseInt(match[0]);
        g = parseInt(match[1]);
        b = parseInt(match[2]);
      }
    }
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
  }, [pageBackgroundColor]);

  // Control colors based on background
  const controlColor = isBackgroundDark ? 'text-white' : 'text-gray-600';
  const controlColorActive = isBackgroundDark ? 'active:text-gray-300' : 'active:text-gray-800';

  // Calculate longest path from a given page (recursive with cycle detection)
  const calculateLongestPath = useMemo(() => {
    const cache: { [pageKey: string]: number } = {};

    const calculate = (pageKey: string, visited: Set<string> = new Set()): number => {
      // Return cached result if available
      if (cache[pageKey] !== undefined) {
        return cache[pageKey];
      }

      // Cycle detection
      if (visited.has(pageKey)) {
        return 0;
      }

      const page = storyPages[pageKey];
      if (!page) {
        return 1;
      }

      // Check if this is an ending (no choices and no nextPage)
      const choices = page.choices || [];
      const hasNextPage = page.nextPage;

      if (choices.length === 0 && !hasNextPage) {
        cache[pageKey] = 1;
        return 1;
      }

      // Add current page to visited set
      const newVisited = new Set(visited);
      newVisited.add(pageKey);

      // Calculate longest path through all possible choices
      let maxPath = 0;

      if (hasNextPage) {
        maxPath = Math.max(maxPath, calculate(hasNextPage, newVisited));
      }

      choices.forEach((choice: any) => {
        if (choice.nextPage) {
          const pathLength = calculate(choice.nextPage, newVisited);
          maxPath = Math.max(maxPath, pathLength);
        }
      });

      const result = 1 + maxPath;
      cache[pageKey] = result;
      return result;
    };

    return calculate;
  }, [storyPages]);

  // Smart progress calculation based on longest path from current position
  const pagesVisited = pageHistory.length;
  const longestPathFromCurrent = useMemo(() => {
    return calculateLongestPath(currentPageKey);
  }, [currentPageKey, calculateLongestPath]);

  const totalEstimatedPath = pagesVisited + longestPathFromCurrent - 1;
  const progressPercentage = Math.min(100, (pagesVisited / totalEstimatedPath) * 100);

  // Lock orientation to landscape on native platforms
  useEffect(() => {
    const isNativePlatform = Capacitor.isNativePlatform();
    let cleanup: (() => void) | undefined;

    if (isNativePlatform) {
      // Dynamically import ScreenOrientation only on native
      // @ts-ignore - Module only available on native builds
      import('@capacitor/screen-orientation').then((module: any) => {
        const ScreenOrientation = module.ScreenOrientation;
        // Lock to landscape when entering reading screen
        ScreenOrientation.lock({ orientation: 'landscape' })
          .catch((e: Error) => console.warn('Could not lock orientation:', e));

        // Set up cleanup function
        cleanup = () => {
          ScreenOrientation.unlock()
            .catch((e: Error) => console.warn('Could not unlock orientation:', e));
        };
      }).catch(() => {
        // Plugin not available, ignore
      });

      // Return cleanup function
      return () => {
        if (cleanup) cleanup();
      };
    }
  }, []);

  // Inject animation styles on mount
  useEffect(() => {
    const styleId = 'reading-animations';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fade-out { from { opacity: 1; } to { opacity: 0; } }
        @keyframes slide-in-left { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes slide-in-top { from { transform: translateY(-100%); } to { transform: translateY(0); } }
        @keyframes slide-in-bottom { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-10px); } 75% { transform: translateX(10px); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes wobble { 0%, 100% { transform: rotate(0deg); } 25% { transform: rotate(-5deg); } 75% { transform: rotate(5deg); } }
        @keyframes flash { 0%, 50%, 100% { opacity: 1; } 25%, 75% { opacity: 0; } }
        @keyframes grow { from { transform: scale(1); } to { transform: scale(1.5); } }
        @keyframes shrink { from { transform: scale(1); } to { transform: scale(0.5); } }
        @keyframes sparkle-effect { 0%, 100% { filter: brightness(1); } 50% { filter: brightness(2) drop-shadow(0 0 10px gold); } }
        @keyframes shake-effect { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-10px); } 75% { transform: translateX(10px); } }
        @keyframes bounce-effect { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
        @keyframes flash-effect { 0%, 50%, 100% { opacity: 1; } 25%, 75% { opacity: 0; } }
        @keyframes pulse-effect { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
        @keyframes spin-effect { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fade-in-effect { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fade-out-effect { from { opacity: 1; } to { opacity: 0; } }
        @keyframes grow-effect { from { transform: scale(1); } to { transform: scale(1.5); } }
        @keyframes shrink-effect { from { transform: scale(1); } to { transform: scale(0.5); } }

        .effect-sparkle { animation: sparkle-effect 0.5s ease-in-out; }
        .effect-shake { animation: shake-effect 0.5s ease-in-out; }
        .effect-bounce { animation: bounce-effect 0.5s ease-in-out; }
        .effect-flash { animation: flash-effect 0.5s ease-in-out; }
        .effect-pulse { animation: pulse-effect 0.5s ease-in-out; }
        .effect-spin { animation: spin-effect 0.5s ease-in-out; }
        .effect-fade-in { animation: fade-in-effect 0.5s ease-in-out; }
        .effect-fade-out { animation: fade-out-effect 0.5s ease-in-out; }
        .effect-grow { animation: grow-effect 0.5s ease-in-out; }
        .effect-shrink { animation: shrink-effect 0.5s ease-in-out; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Handle background music (unified approach for web and native)
  useEffect(() => {
    const bgMusicConfig = currentPage?.content?.audio?.backgroundMusic;
    const bgMusicUrl = bgMusicConfig?.url ? getPublicStorageUrl(bgMusicConfig.url) : null;

    // Stop previous music
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    if (bgMusicUrl && !isMuted) {
      const audio = new Audio(bgMusicUrl);
      audio.volume = bgMusicConfig?.volume || 0.5;
      audio.loop = bgMusicConfig?.loop !== false; // Default to loop

      // For iOS: ensure audio plays
      audio.setAttribute('playsinline', 'true');
      audio.setAttribute('webkit-playsinline', 'true');

      audioRef.current = audio;

      // Load then play for better iOS compatibility
      audio.load();
      audio.play().catch(err => {
        console.error('❌ Error playing background music:', err);
      });
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [currentPage?.content?.audio?.backgroundMusic, isMuted]);

  // Cleanup audio refs on unmount
  useEffect(() => {
    return () => {
      Object.values(eventAudioRefs.current).forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
      eventAudioRefs.current = {};
    };
  }, []);

  // Écouter les changements de taille de fenêtre
  useEffect(() => {
    const handleResize = () => setWindowSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculer le scale synchronement — pas de frame de retard entre changement d'orientation
  const canvasScale = useMemo(() => {
    const isNativePlatform = Capacitor.isNativePlatform();

    if (isNativePlatform) {
      const availableWidth = windowSize.w - 96;
      const availableHeight = windowSize.h - 16;
      return Math.min(availableWidth / PAGE_DIMENSIONS.WIDTH, availableHeight / PAGE_DIMENSIONS.HEIGHT);
    } else {
      const uiHeight = 48 + 24 + 40 + 16;
      const availableWidth = windowSize.w - 160;
      const availableHeight = windowSize.h - uiHeight;
      return Math.min(availableWidth / PAGE_DIMENSIONS.WIDTH, availableHeight / PAGE_DIMENSIONS.HEIGHT, 1.5);
    }
  }, [PAGE_DIMENSIONS.WIDTH, PAGE_DIMENSIONS.HEIGHT, windowSize]);

  // Execute action
  const executeAction = (action: any, elementId: string) => {
    const config = action.config;

    switch (action.type) {
      case 'playSound':
        if (config.soundUrl) {
          // Debounce: ignorer si appelé dans les 500ms précédentes (éléments draggables déclenchent 2 events)
          const now = Date.now();
          const lastTime = lastSoundActionTime.current[elementId] || 0;
          if (now - lastTime < 500) {
            break;
          }
          lastSoundActionTime.current[elementId] = now;

          const soundUrl = getPublicStorageUrl(config.soundUrl);
          const prevAudio = eventAudioRefs.current[elementId];

          // Toggle: si l'audio joue déjà, on l'arrête
          if (prevAudio) {
            if (!prevAudio.paused && !prevAudio.ended) {
              prevAudio.pause();
              prevAudio.currentTime = 0;
              break;
            }
            prevAudio.pause();
            prevAudio.currentTime = 0;
          }

          // Créer et jouer le nouvel audio
          const audio = new Audio(soundUrl);
          audio.volume = config.soundVolume ?? 1;
          audio.loop = config.soundLoop ?? false;
          audio.setAttribute('playsinline', 'true');
          audio.setAttribute('webkit-playsinline', 'true');
          eventAudioRefs.current[elementId] = audio;
          audio.load();
          audio.play().catch(err => {
            console.error('Error playing sound:', err);
            setTimeout(() => {
              audio.play().catch(e => console.error('Retry failed:', e));
            }, 100);
          });
        }
        break;

      case 'visualEffect':
        if (config.effect) {
          setActiveEffects(prev => ({ ...prev, [elementId]: config.effect! }));
          const duration = config.effectDuration || 500;
          setTimeout(() => {
            setActiveEffects(prev => {
              const newEffects = { ...prev };
              delete newEffects[elementId];
              return newEffects;
            });
          }, duration);
        }
        break;

      case 'showMessage':
        if (config.message) {
          const msg = config.message[language] || config.message.fr || '';
          if (msg) {
            setToastMessage(msg);
            const duration = config.messageDuration ?? 3000;
            if (duration > 0) {
              setTimeout(() => setToastMessage(null), duration);
            }
          }
        }
        break;

      case 'navigate':
        if (config.targetPageId) {
          navigateToPage(config.targetPageId);
        }
        break;

      case 'grantReward':
        if (config.rewardElementId) {
          console.log(`Reward granted: ${config.rewardElementType} #${config.rewardElementId}`);
          // TODO: Add reward to user's collection
        }
        break;

      case 'triggerAnimation':
        console.log('Trigger animation action (not fully implemented)');
        break;
    }
  };

  // Handle element events
  const handleElementEvent = (element: any, eventType: 'onClick' | 'onHover' | 'onLoad' | 'onDragStart' | 'onDragEnd') => {
    const interactive = element.interactive;
    if (!interactive?.events?.[eventType]) return;

    const actions = interactive.events[eventType];
    actions.forEach((action: any) => {
      executeAction(action, element.id);
    });
  };

  // Trigger onLoad events when page changes
  useEffect(() => {
    if (!currentPage?.content?.elements) return;

    currentPage.content.elements.forEach((element: any) => {
      if (element.interactive?.events?.onLoad) {
        handleElementEvent(element, 'onLoad');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Border effect styles
  const getBorderEffectStyle = (effect: string) => {
    switch (effect) {
      case 'fade-radial':
        return {
          mask: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
          WebkitMask: 'radial-gradient(ellipse at center, black 30%, transparent 70%)'
        };
      case 'fade-soft':
        return {
          mask: 'radial-gradient(ellipse at center, black 60%, transparent 85%)',
          WebkitMask: 'radial-gradient(ellipse at center, black 60%, transparent 85%)'
        };
      case 'fade-linear':
        return {
          mask: 'linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)',
          WebkitMask: 'linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)',
          maskComposite: 'intersect',
          WebkitMaskComposite: 'source-in'
        } as React.CSSProperties;
      case 'fade-blur':
        return {
          mask: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)',
          WebkitMask: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)',
          maskComposite: 'intersect',
          WebkitMaskComposite: 'source-in'
        } as React.CSSProperties;
      case 'torn-edges':
        return {
          clipPath: 'polygon(0% 5%, 10% 0%, 25% 8%, 40% 2%, 55% 7%, 70% 0%, 85% 5%, 100% 3%, 95% 15%, 100% 30%, 90% 45%, 100% 60%, 85% 75%, 100% 90%, 95% 100%, 75% 95%, 60% 100%, 45% 90%, 30% 100%, 15% 95%, 0% 90%, 5% 75%, 0% 60%, 10% 45%, 0% 30%, 5% 15%)'
        };
      case 'brush-strokes':
        return {
          mask: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath d='M0,0 Q20,10 40,5 Q60,0 80,8 Q90,15 100,20 L100,80 Q80,90 60,95 Q40,100 20,90 Q10,85 0,80 Z' fill='black'/%3E%3C/svg%3E")`,
          WebkitMask: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath d='M0,0 Q20,10 40,5 Q60,0 80,8 Q90,15 100,20 L100,80 Q80,90 60,95 Q40,100 20,90 Q10,85 0,80 Z' fill='black'/%3E%3C/svg%3E")`
        };
      case 'blur-edges':
        return {
          mask: 'radial-gradient(ellipse at center, black 40%, rgba(0,0,0,0.8) 60%, transparent 80%)',
          WebkitMask: 'radial-gradient(ellipse at center, black 40%, rgba(0,0,0,0.8) 60%, transparent 80%)',
          filter: 'blur(0.5px)'
        };
      case 'vintage-photo':
        return {
          border: '8px solid #f4f1e8',
          boxShadow: 'inset 0 0 20px rgba(0,0,0,0.3), 0 0 20px rgba(0,0,0,0.2)',
          filter: 'sepia(0.3) contrast(1.1)'
        };
      case 'geometric-cut':
        return {
          clipPath: 'polygon(0 15%, 15% 0, 85% 0, 100% 15%, 100% 85%, 85% 100%, 15% 100%, 0 85%)'
        };
      default:
        return {};
    }
  };

  // Helper for padding style
  const getPaddingStyle = (padding: number | { top: number; right: number; bottom: number; left: number } | undefined) => {
    if (!padding) return '0px';
    if (typeof padding === 'number') return `${padding}px`;
    return `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px`;
  };

  // Helper pour obtenir les styles de cadre décoratif (simple, terminal, parchment, neon)
  const getFrameStyles = (element: any): React.CSSProperties => {
    const frameStyle = element.frameStyle;
    if (!frameStyle?.enabled) return {};

    const style = element.style;
    const userPadding = typeof style.padding === 'number' ? style.padding : 0;
    const userBorderColor = style.borderColor;
    const userBorderWidth = style.borderWidth;
    const userBorderRadius = style.borderRadius;

    // Valeurs par défaut selon le type de cadre
    const defaults: Record<string, { padding: number; borderColor: string; borderWidth: number; borderRadius: number }> = {
      simple: { padding: 12, borderColor: '#000000', borderWidth: 2, borderRadius: 12 },
      terminal: { padding: 16, borderColor: '#00ff00', borderWidth: 3, borderRadius: 4 },
      parchment: { padding: 16, borderColor: '#8b4513', borderWidth: 3, borderRadius: 4 },
      neon: { padding: 14, borderColor: '#ff00ff', borderWidth: 2, borderRadius: 8 },
      texture: { padding: 16, borderColor: 'transparent', borderWidth: 0, borderRadius: 12 }
    };

    const frameDefaults = defaults[frameStyle.type] || defaults.simple;

    const effectivePadding = userPadding > 0 ? userPadding : frameDefaults.padding;
    const effectiveBorderColor = userBorderColor || frameDefaults.borderColor;
    const effectiveBorderWidth = userBorderWidth !== undefined ? userBorderWidth : frameDefaults.borderWidth;
    const effectiveBorderRadius = userBorderRadius !== undefined ? userBorderRadius : frameDefaults.borderRadius;

    const baseFrameStyle: React.CSSProperties = {
      padding: `${effectivePadding}px`,
      border: `${effectiveBorderWidth}px solid ${effectiveBorderColor}`,
      borderRadius: `${effectiveBorderRadius}px`
    };

    switch (frameStyle.type) {
      case 'simple':
        return {
          backgroundColor: style.backgroundColor || '#ffffff',
          ...baseFrameStyle
        };

      case 'terminal':
        return {
          backgroundColor: '#0a0a0a',
          boxShadow: `inset 0 0 20px rgba(0, 255, 0, 0.1), 0 0 10px ${effectiveBorderColor}40`,
          ...baseFrameStyle
        };

      case 'parchment':
        return {
          backgroundColor: '#f4e4bc',
          boxShadow: 'inset 0 0 30px rgba(139, 69, 19, 0.2), 2px 2px 8px rgba(0, 0, 0, 0.3)',
          backgroundImage: 'linear-gradient(135deg, rgba(139, 69, 19, 0.05) 0%, transparent 50%, rgba(139, 69, 19, 0.08) 100%)',
          ...baseFrameStyle
        };

      case 'neon':
        return {
          backgroundColor: 'rgba(10, 10, 30, 0.95)',
          boxShadow: `0 0 5px ${effectiveBorderColor}, 0 0 10px ${effectiveBorderColor}, 0 0 20px ${effectiveBorderColor}, inset 0 0 15px ${effectiveBorderColor}20`,
          ...baseFrameStyle
        };

      case 'texture':
        return {
          backgroundImage: frameStyle.textureUrl ? `url(${frameStyle.textureUrl})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          ...baseFrameStyle
        };

      default:
        return {};
    }
  };

  // Animation style
  const getAnimationStyle = (animation: any, element: any): React.CSSProperties => {
    if (!animation || !animation.type || animation.type === 'none') return {};

    const { type, duration = 1000, delay = 0, loop = false } = animation;

    // move-to animation with dynamic keyframes
    if (type === 'move-to' && animation.targetPosition) {
      const elementCenterX = element.position.x + element.size.width / 2;
      const elementCenterY = element.position.y + element.size.height / 2;
      const deltaX = animation.targetPosition.x - elementCenterX;
      const deltaY = animation.targetPosition.y - elementCenterY;

      const rotation = element.rotation || 0;
      const perspective3D = (element.type === 'text' && element.style?.perspective3D) ? element.style.perspective3D : 0;

      const buildTransform = (translateX: number, translateY: number) => {
        let transform = `translate(${translateX}px, ${translateY}px)`;
        if (rotation) transform += ` rotate(${rotation}deg)`;
        if (perspective3D > 0) transform += ` rotateX(${perspective3D}deg)`;
        return transform;
      };

      if (animation.returnToStart) {
        const keyframeName = `move-to-${element.id}`;
        const existingStyle = document.getElementById(keyframeName);
        if (existingStyle) existingStyle.remove();

        const styleEl = document.createElement('style');
        styleEl.id = keyframeName;
        styleEl.textContent = `
          @keyframes ${keyframeName} {
            0% { transform: ${buildTransform(0, 0)}; }
            50% { transform: ${buildTransform(deltaX, deltaY)}; }
            100% { transform: ${buildTransform(0, 0)}; }
          }
        `;
        document.head.appendChild(styleEl);

        return {
          animation: `${keyframeName} ${duration * 2}ms ease-in-out ${delay}ms ${loop ? 'infinite' : '1'}`,
          transformStyle: perspective3D > 0 ? 'preserve-3d' : undefined,
        };
      } else {
        const keyframeName = `move-to-${element.id}-oneway`;
        const existingStyle = document.getElementById(keyframeName);
        if (existingStyle) existingStyle.remove();

        const styleEl = document.createElement('style');
        styleEl.id = keyframeName;
        styleEl.textContent = `
          @keyframes ${keyframeName} {
            0% { transform: ${buildTransform(0, 0)}; }
            100% { transform: ${buildTransform(deltaX, deltaY)}; }
          }
        `;
        document.head.appendChild(styleEl);

        return {
          animation: `${keyframeName} ${duration}ms ease-in-out ${delay}ms ${loop ? 'infinite' : '1 forwards'}`,
          transformStyle: perspective3D > 0 ? 'preserve-3d' : undefined,
        };
      }
    }

    // Custom keyframe animation
    if (animation.customConfig) {
      const { css, animationValue } = generateCustomKeyframes(
        element.id,
        {
          ...animation.customConfig,
          duration,
          delay,
          loop,
          reverse: animation.customConfig.reverse,
        },
        animation.movementOffset
      );
      const styleId = `custom-anim-style-${element.id}`;
      const existing = document.getElementById(styleId);
      if (existing) existing.remove();
      const styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.textContent = css;
      document.head.appendChild(styleEl);
      return { animation: animationValue };
    }

    // Standard CSS animation (legacy)
    return {
      animation: `${type} ${duration}ms ease-in-out ${delay}ms ${loop ? 'infinite' : '1'}`,
    };
  };

  // Drag handlers
  const handleDragStart = (element: any, clientX: number, clientY: number) => {
    if (!element.interactive?.draggable) return;

    const canvasRect = contentRef.current?.getBoundingClientRect();
    if (!canvasRect) return;

    const currentPos = elementPositions[element.id] || element.position;
    const posXInCanvas = (clientX - canvasRect.left) / canvasScale;
    const posYInCanvas = (clientY - canvasRect.top) / canvasScale;

    const offsetX = posXInCanvas - currentPos.x;
    const offsetY = posYInCanvas - currentPos.y;

    setDraggedElement(element.id);
    setDragOffset({ x: offsetX, y: offsetY });
    setHasDragged(false);
  };

  const handleMouseDragStart = (element: any, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    handleDragStart(element, e.clientX, e.clientY);
  };

  const handleTouchDragStart = (element: any, e: React.TouchEvent) => {
    e.stopPropagation();
    if (e.touches.length === 1) {
      handleDragStart(element, e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  const handleDragMoveCoords = React.useCallback((clientX: number, clientY: number) => {
    if (!draggedElement || !contentRef.current) return;

    const canvasRect = contentRef.current.getBoundingClientRect();
    const posXInCanvas = (clientX - canvasRect.left) / canvasScale;
    const posYInCanvas = (clientY - canvasRect.top) / canvasScale;

    const x = posXInCanvas - dragOffset.x;
    const y = posYInCanvas - dragOffset.y;

    setHasDragged(true);

    setElementPositions(prev => ({
      ...prev,
      [draggedElement]: { x, y }
    }));
  }, [draggedElement, dragOffset, canvasScale]);

  const handleDragMove = React.useCallback((e: MouseEvent) => {
    handleDragMoveCoords(e.clientX, e.clientY);
  }, [handleDragMoveCoords]);

  const handleTouchDragMove = React.useCallback((e: TouchEvent) => {
    if (e.touches.length === 1) {
      e.preventDefault(); // Prevent scrolling while dragging
      handleDragMoveCoords(e.touches[0].clientX, e.touches[0].clientY);
    }
  }, [handleDragMoveCoords]);

  const handleDragEnd = React.useCallback(() => {
    if (!draggedElement) return;

    const element = currentPage?.content?.elements?.find((el: any) => el.id === draggedElement);

    if (hasDragged) {
      if (element?.interactive?.events?.onDragEnd) {
        handleElementEvent(element, 'onDragEnd');
      }
      if (element?.interactive?.events?.onDragStart) {
        handleElementEvent(element, 'onDragStart');
      }
    } else {
      if (element?.interactive?.events?.onClick) {
        handleElementEvent(element, 'onClick');
      }
    }

    setDraggedElement(null);
    setHasDragged(false);
  }, [draggedElement, hasDragged, currentPage?.content?.elements]);

  useEffect(() => {
    if (draggedElement) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      document.addEventListener('touchmove', handleTouchDragMove, { passive: false });
      document.addEventListener('touchend', handleDragEnd);
      document.addEventListener('touchcancel', handleDragEnd);
      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
        document.removeEventListener('touchmove', handleTouchDragMove);
        document.removeEventListener('touchend', handleDragEnd);
        document.removeEventListener('touchcancel', handleDragEnd);
      };
    }
  }, [draggedElement, handleDragMove, handleDragEnd, handleTouchDragMove]);

  // Use non-passive touch listeners for pinch-to-zoom on iOS (must be before early returns)
  useEffect(() => {
    const container = zoomContainerRef.current;
    if (!container || !isNative) return;

    const onTouchStart = (e: TouchEvent) => {
      // ONLY preventDefault for 2-finger touches (zoom), allow 1-finger for drag
      if (e.touches.length === 2) {
        e.preventDefault();
        initialPinchDistance.current = getDistance(e.touches as unknown as React.TouchList);
        initialZoom.current = userZoom;
        pinchCenter.current = getPinchCenter(e.touches as unknown as React.TouchList);
        initialScrollPos.current = {
          x: container.scrollLeft,
          y: container.scrollTop
        };
      }
      // Let 1-finger touches pass through for draggable elements
    };

    const onTouchMove = (e: TouchEvent) => {
      // ONLY preventDefault for 2-finger pinch, not for 1-finger drag
      if (e.touches.length === 2 && initialPinchDistance.current !== null) {
        e.preventDefault();
        const currentDistance = getDistance(e.touches as unknown as React.TouchList);
        const currentCenter = getPinchCenter(e.touches as unknown as React.TouchList);
        const scale = currentDistance / initialPinchDistance.current;
        const newZoom = Math.min(Math.max(initialZoom.current * scale, 1), 3);

        const panDeltaX = pinchCenter.current.x - currentCenter.x;
        const panDeltaY = pinchCenter.current.y - currentCenter.y;

        setUserZoom(newZoom);

        const rect = container.getBoundingClientRect();
        const zoomRatio = newZoom / initialZoom.current;
        const viewportX = pinchCenter.current.x - rect.left;
        const viewportY = pinchCenter.current.y - rect.top;
        const contentX = initialScrollPos.current.x + viewportX;
        const contentY = initialScrollPos.current.y + viewportY;
        const newScrollX = (contentX * zoomRatio) - viewportX + panDeltaX;
        const newScrollY = (contentY * zoomRatio) - viewportY + panDeltaY;

        container.scrollLeft = Math.max(0, newScrollX);
        container.scrollTop = Math.max(0, newScrollY);
      }
      // Let 1-finger moves pass through for draggable elements
    };

    const onTouchEnd = () => {
      initialPinchDistance.current = null;
    };

    // Use passive: false to allow preventDefault on 2-finger touches
    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd);

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
    };
  }, [isNative, userZoom]);

  // Get interactive props
  const getInteractiveProps = (element: any) => {
    const hasInteractivity = element.interactive?.events || element.interactive?.draggable;
    if (!hasInteractivity) return {};

    const props: any = {};
    const effectClass = activeEffects[element.id] ? `effect-${activeEffects[element.id]}` : '';

    if (effectClass) {
      props.className = effectClass;
    }

    if (element.interactive?.draggable) {
      props.onMouseDown = (e: React.MouseEvent) => handleMouseDragStart(element, e);
      props.onTouchStart = (e: React.TouchEvent) => handleTouchDragStart(element, e);
      props.draggable = false;
      props.onDragStart = (e: React.DragEvent) => e.preventDefault();
      props.style = {
        ...props.style,
        cursor: draggedElement === element.id ? 'grabbing' : 'grab',
        userSelect: 'none' as const,
        WebkitUserSelect: 'none' as const,
        WebkitUserDrag: 'none' as const,
        WebkitTouchCallout: 'none' as const, // Prevent iOS context menu on hold
        touchAction: 'none' as const, // Prevent default touch behavior
      };
    }

    if (element.interactive?.events?.onClick && !element.interactive?.draggable) {
      props.onClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        handleElementEvent(element, 'onClick');
      };
      props.style = { ...props.style, cursor: 'pointer' };
    }

    if (element.interactive?.events?.onHover) {
      props.onMouseEnter = () => handleElementEvent(element, 'onHover');
    }

    return props;
  };

  // Render element
  // Calculer le filtre compo une seule fois
  const compoFilterStyle = (() => {
    const compo = currentPage?.compoEffect;
    if (!compo?.enabled) return {};
    const filters: string[] = [];
    if (compo.brightness !== undefined && compo.brightness !== 100) filters.push(`brightness(${compo.brightness}%)`);
    if (compo.contrast !== undefined && compo.contrast !== 100) filters.push(`contrast(${compo.contrast}%)`);
    if (compo.saturation !== undefined && compo.saturation !== 100) filters.push(`saturate(${compo.saturation}%)`);
    if (compo.hueRotate) filters.push(`hue-rotate(${compo.hueRotate}deg)`);
    if (compo.sepia) filters.push(`sepia(${compo.sepia}%)`);
    if (compo.blur) filters.push(`blur(${compo.blur}px)`);
    const result: React.CSSProperties = {};
    if (filters.length > 0) result.filter = filters.join(' ');
    if (compo.opacity !== undefined && compo.opacity !== 1) result.opacity = compo.opacity;
    return result;
  })();

  const renderElement = (element: any) => {
    if (!element.visible) return null;

    const { position, size, type, rotation = 0, zIndex = 1, animation } = element;

    const currentPosition = elementPositions[element.id] || position;

    // Vérifier si autoFitHeight est activé (pour les éléments texte)
    // Auto-fit est maintenant géré par AutoTextCarousel, pas par baseStyle
    const isAutoFitHeight = false;

    // Appliquer l'effet de compo aux éléments non exclus
    const elementCompoStyle = (!element.excludeFromCompo && currentPage?.compoEffect?.enabled) ? compoFilterStyle : {};

    const baseStyle: React.CSSProperties = {
      position: 'absolute',
      left: `${currentPosition.x}px`,
      top: `${currentPosition.y}px`,
      width: `${size.width}px`,
      height: isAutoFitHeight ? 'auto' : `${size.height}px`,
      transform: (animation?.type === 'move-to') ? undefined : `rotate(${rotation}deg)`,
      zIndex: draggedElement === element.id ? 9999 : zIndex,
      ...getAnimationStyle(animation, element),
      ...elementCompoStyle,
      // Drop shadow (sauf shapes, géré sur le div enfant)
      ...(element.dropShadow && type !== 'shape' ? (() => {
        const ds = element.dropShadow;
        const r = parseInt(ds.color.slice(1, 3), 16) || 0;
        const g = parseInt(ds.color.slice(3, 5), 16) || 0;
        const b = parseInt(ds.color.slice(5, 7), 16) || 0;
        return { filter: `drop-shadow(${ds.offsetX}px ${ds.offsetY}px ${ds.blur}px rgba(${r},${g},${b},${ds.opacity}))` };
      })() : {}),
      // Blend mode
      ...(element.blendMode ? { mixBlendMode: element.blendMode as any } : {}),
    };

    const interactiveProps = getInteractiveProps(element);
    const elementProps = {
      ...interactiveProps,
      style: { ...baseStyle, ...interactiveProps.style },
    };

    switch (type) {
      case 'background': {
        const bg = element;
        let background = bg.style.fillColor;

        if (bg.style.gradient && bg.style.gradient.type !== 'none') {
          const { gradient } = bg.style;
          if (gradient.type === 'linear') {
            const cssDir = (gradient.direction || 'to-bottom').replace(/-/g, ' ');
            background = `linear-gradient(${cssDir}, ${gradient.startColor || '#3b82f6'}, ${gradient.endColor || '#ffffff'})`;
          } else if (gradient.type === 'radial') {
            background = `radial-gradient(circle, ${gradient.startColor || '#3b82f6'}, ${gradient.endColor || '#ffffff'})`;
          }
        }

        // Utiliser la position et taille réelles de l'élément (pas forcer plein écran)
        return (
          <div
            key={element.id}
            {...elementProps}
            style={{
              ...baseStyle,
              background,
              opacity: bg.style.opacity !== undefined ? bg.style.opacity : 1,
              mixBlendMode: element.blendMode as any || undefined,
            }}
          />
        );
      }

      case 'text': {
        const txt = element;
        const { style, richContent, content, verticalAlign } = txt;

        // Mode auto : position/taille calculées depuis les %
        if (txt.layoutMode === 'auto' && txt.autoLayout) {
          const auto = txt.autoLayout;
          const cw = PAGE_DIMENSIONS.WIDTH;
          const ch = PAGE_DIMENSIONS.HEIGHT;
          const w = Math.round(cw * auto.widthPercent / 100);
          const h = Math.round(ch * auto.maxHeightPercent / 100);
          const x = Math.round((cw - w) / 2);
          const maxY = ch - h;
          const y = Math.round(maxY * auto.verticalPercent / 100);

          // Auto-fit anchor : positionner le bloc réduit dans son espace
          const autoFitAnchor = txt.autoFitAnchor || 'top';

          return (
            <div
              key={element.id}
              {...elementProps}
              style={{
                ...baseStyle,
                position: 'absolute',
                left: `${(x / cw) * 100}%`,
                top: `${(y / ch) * 100}%`,
                width: `${(w / cw) * 100}%`,
                height: `${(h / ch) * 100}%`,
                zIndex,
                pointerEvents: 'auto',
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: txt.autoFitHeight
                  ? (autoFitAnchor === 'bottom' ? 'flex-end' : autoFitAnchor === 'center' ? 'center' : 'flex-start')
                  : 'flex-start',
              }}
            >
              <AutoTextCarousel
                ref={(r) => { autoTextCarouselRefs.current[element.id] = r; }}
                richContent={richContent}
                plainContent={content?.[language] || content?.fr}
                language={language}
                width={w}
                height={h}
                style={{
                  fontSize: style.fontSize,
                  fontFamily: style.fontFamily,
                  color: style.color,
                  textAlign: style.textAlign,
                  fontWeight: style.fontWeight as any,
                  lineHeight: style.lineHeight,
                  letterSpacing: style.letterSpacing,
                  textShadow: style.textShadow || (style.shadow
                    ? `${style.shadow.x}px ${style.shadow.y}px ${style.shadow.blur}px ${style.shadow.color}`
                    : undefined),
                  backgroundColor: style.backgroundColor,
                  borderColor: style.borderColor,
                  borderWidth: style.borderWidth,
                  borderRadius: style.borderRadius,
                  padding: typeof style.padding === 'number' ? style.padding : 0,
                }}
                autoFitHeight={txt.autoFitHeight}
                fontSizeAuto={auto.fontSizeAuto}
                fontSizeMin={auto.fontSizeMin}
                fontSizeMax={auto.fontSizeMax}
                verticalAlign={verticalAlign}
              />
            </div>
          );
        }

        // Mode fixe : carousel si le texte déborde (même mécanisme que l'auto)
        if (txt.layoutMode !== 'auto') {
          const fixedAutoFitAnchor = txt.autoFitAnchor || 'top';
          return (
            <div
              key={element.id}
              {...elementProps}
              style={{
                ...baseStyle,
                zIndex,
                pointerEvents: 'auto',
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: txt.autoFitHeight
                  ? (fixedAutoFitAnchor === 'bottom' ? 'flex-end' : fixedAutoFitAnchor === 'center' ? 'center' : 'flex-start')
                  : 'flex-start',
              }}
            >
              <AutoTextCarousel
                ref={(r) => { autoTextCarouselRefs.current[element.id] = r; }}
                richContent={richContent}
                plainContent={content?.[language] || content?.fr}
                language={language}
                width={element.size.width}
                height={element.size.height}
                style={{
                  fontSize: style.fontSize,
                  fontFamily: style.fontFamily,
                  color: style.color,
                  textAlign: style.textAlign,
                  fontWeight: style.fontWeight as any,
                  lineHeight: style.lineHeight,
                  letterSpacing: style.letterSpacing,
                  textShadow: style.textShadow || (style.shadow
                    ? `${style.shadow.x}px ${style.shadow.y}px ${style.shadow.blur}px ${style.shadow.color}`
                    : undefined),
                  backgroundColor: style.backgroundColor,
                  borderColor: style.borderColor,
                  borderWidth: style.borderWidth,
                  borderRadius: style.borderRadius,
                  padding: typeof style.padding === 'number' ? style.padding : 0,
                }}
                autoFitHeight={txt.autoFitHeight}
                verticalAlign={verticalAlign}
              />
            </div>
          );
        }

        const basePerspective = style.perspective3D || 0;
        const needsPerspectiveWrapper = basePerspective !== 0 && animation?.type !== 'move-to';

        // Obtenir les styles de cadre décoratif (simple, terminal, parchment, neon)
        const frameStyles = getFrameStyles(txt);
        const hasFrame = txt.frameStyle?.enabled;

        const textStyle: React.CSSProperties = {
          fontSize: `${style.fontSize}px`,
          fontFamily: style.fontFamily,
          color: style.color,
          textAlign: style.textAlign,
          lineHeight: style.lineHeight ? `${style.lineHeight}` : undefined,
          letterSpacing: style.letterSpacing ? `${style.letterSpacing}px` : undefined,
          wordWrap: style.wordWrap ? 'break-word' : undefined,
          // Priorité à textShadow (string CSS directe), sinon fallback sur shadow (objet legacy)
          textShadow: style.textShadow || (style.shadow
            ? `${style.shadow.x}px ${style.shadow.y}px ${style.shadow.blur}px ${style.shadow.color}`
            : undefined),
          // Styles généraux du bloc (bold, italic, underline)
          fontWeight: style.fontWeight || 'normal',
          fontStyle: style.fontStyle || 'normal',
          textDecoration: style.textDecoration || 'none',
          textWrap: 'balance' as any,
        };

        const renderTextContent = () => {
          if (richContent && richContent[language]) {
            const html = lexicalToHtml(richContent[language]);

            // Déterminer si le bloc a un style actif (pour inversion inline)
            const blockIsBold = style.fontWeight === 'bold';
            const blockIsItalic = style.fontStyle === 'italic';
            const blockIsUnderline = style.textDecoration === 'underline';

            // Générer un ID unique pour scoper les styles CSS
            const scopeId = `text-${txt.id}`;

            return (
              <>
                {/* Styles CSS pour inversion du formatage inline */}
                <style>{`
                  #${scopeId} p { margin: 0; padding: 0; min-height: 1em; }
                  #${scopeId} p + p { margin-top: 0.5em; }
                  #${scopeId} strong { font-weight: ${blockIsBold ? 'normal' : 'bold'} !important; }
                  #${scopeId} em { font-style: ${blockIsItalic ? 'normal' : 'italic'} !important; }
                  #${scopeId} u { text-decoration: ${blockIsUnderline ? 'none' : 'underline'} !important; }
                `}</style>
                <div
                  id={scopeId}
                  style={textStyle}
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              </>
            );
          }

          const legacyTextStyle: React.CSSProperties = {
            ...textStyle,
            fontWeight: style.bold || style.fontWeight === 'bold' ? 'bold' : style.fontWeight || 'normal',
            fontStyle: style.italic || style.fontStyle === 'italic' ? 'italic' : style.fontStyle || 'normal',
            textDecoration: style.underline || style.textDecoration === 'underline' ? 'underline' : style.textDecoration || 'none',
            whiteSpace: 'pre-wrap',
          };

          return (
            <div style={legacyTextStyle}>
              {content?.[language] || content?.fr || ''}
            </div>
          );
        };

        const containerStyle: React.CSSProperties = {
          ...baseStyle,
          // Appliquer les styles de base seulement si pas de cadre décoratif
          ...(!hasFrame && {
            backgroundColor: style.backgroundColor,
            borderColor: style.borderColor,
            borderWidth: style.borderWidth ? `${style.borderWidth}px` : undefined,
            borderStyle: style.borderWidth ? 'solid' : undefined,
            borderRadius: style.borderRadius ? `${style.borderRadius}px` : undefined,
            padding: getPaddingStyle(style.padding),
          }),
          // Appliquer les styles de cadre décoratif (override les valeurs ci-dessus)
          ...frameStyles,
          overflow: style.overflow || 'visible',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: verticalAlign === 'top' ? 'flex-start' : verticalAlign === 'bottom' ? 'flex-end' : 'center',
        };

        if (needsPerspectiveWrapper) {
          const perspectiveWrapperStyle: React.CSSProperties = {
            ...baseStyle,
            perspective: '600px',
            perspectiveOrigin: 'center center',
          };

          const innerContainerStyle: React.CSSProperties = {
            // Appliquer les styles de base seulement si pas de cadre décoratif
            ...(!hasFrame && {
              backgroundColor: style.backgroundColor,
              borderColor: style.borderColor,
              borderWidth: style.borderWidth ? `${style.borderWidth}px` : undefined,
              borderStyle: style.borderWidth ? 'solid' : undefined,
              borderRadius: style.borderRadius ? `${style.borderRadius}px` : undefined,
              padding: getPaddingStyle(style.padding),
            }),
            // Appliquer les styles de cadre décoratif
            ...frameStyles,
            overflow: style.overflow || 'visible',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: verticalAlign === 'top' ? 'flex-start' : verticalAlign === 'bottom' ? 'flex-end' : 'center',
            width: '100%',
            height: txt.autoFitHeight ? 'auto' : '100%',
            transform: `rotateX(${basePerspective}deg)`,
            transformStyle: 'preserve-3d',
          };

          return (
            <div key={element.id} {...elementProps} style={perspectiveWrapperStyle}>
              <div style={innerContainerStyle}>
                {renderTextContent()}
              </div>
            </div>
          );
        }

        // Auto-fit avec ancrage vertical centre/bas : wrapper
        const autoFitAnchor = txt.autoFitAnchor || 'top';
        if (txt.autoFitHeight && autoFitAnchor !== 'top') {
          return (
            <div key={element.id} {...elementProps} style={{
              ...baseStyle,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: autoFitAnchor === 'bottom' ? 'flex-end' : 'center',
              overflow: 'visible',
            }}>
              <div style={{
                ...containerStyle,
                position: 'relative',
                left: 0,
                top: 0,
                width: '100%',
                height: 'auto',
              }}>
                {renderTextContent()}
              </div>
            </div>
          );
        }

        return (
          <div key={element.id} {...elementProps} style={containerStyle}>
            {renderTextContent()}
          </div>
        );
      }

      case 'bubble': {
        const bubble = element;
        const { style, content } = bubble;

        return (
          <div
            key={element.id}
            {...elementProps}
            style={{
              ...baseStyle,
              backgroundColor: style.backgroundColor,
              border: `${style.borderWidth}px solid ${style.borderColor}`,
              borderRadius: '15px',
              padding: '10px',
              fontSize: `${style.fontSize}px`,
              fontFamily: style.fontFamily,
              color: style.color,
              position: 'relative',
            }}
          >
            {content[language] || content.fr}
            <div
              style={{
                position: 'absolute',
                width: 0,
                height: 0,
                borderLeft: '10px solid transparent',
                borderRight: '10px solid transparent',
                borderTop: `15px solid ${style.backgroundColor}`,
                bottom: '-15px',
                left: style.tailPosition === 'bottom-left' ? '20px' :
                     style.tailPosition === 'bottom-right' ? 'calc(100% - 40px)' : '50%',
              }}
            />
          </div>
        );
      }

      case 'image': {
        const img = element;

        // Outline via SVG filter (feMorphology dilate) — performant sur Chrome
        const outlineId = img.outline?.width ? `outline-fr-${element.id}` : '';
        const outlineSvg = img.outline?.width ? (
          <svg style={{ position: 'absolute', width: 0, height: 0, pointerEvents: 'none' }}>
            <defs>
              <filter id={outlineId} x="-20%" y="-20%" width="140%" height="140%">
                <feMorphology in="SourceAlpha" operator="dilate" radius={img.outline!.width} result="dilated" />
                <feFlood floodColor={img.outline!.color} result="color" />
                <feComposite in="color" in2="dilated" operator="in" result="outline" />
                <feMerge>
                  <feMergeNode in="outline" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
          </svg>
        ) : null;
        const outlineFilter = outlineId ? `url(#${outlineId})` : '';

        let filterStyle = '';
        const blurAmount = img.filters?.blur || 0;
        if (img.filters) {
          const f = img.filters;
          filterStyle = `brightness(${f.brightness}%) contrast(${f.contrast}%) saturate(${f.saturation}%)${f.hueRotate ? ` hue-rotate(${f.hueRotate}deg)` : ''} blur(${f.blur}px)`;
        }
        if (outlineFilter) {
          filterStyle = filterStyle ? `${filterStyle} ${outlineFilter}` : outlineFilter;
        }
        // Color overlay
        const colorOverlayId = (element as any).colorOverlay ? `color-overlay-fr-${element.id}` : '';
        if (colorOverlayId) {
          filterStyle = filterStyle ? `${filterStyle} url(#${colorOverlayId})` : `url(#${colorOverlayId})`;
        }

        // Parallax effect
        let parallaxTransform = '';
        if (img.parallaxEnabled) {
          const multiplier = getParallaxMultiplier(img.parallaxLayer);
          const offsetX = parallaxOffset.x * multiplier * PAGE_DIMENSIONS.WIDTH;
          const offsetY = parallaxOffset.y * multiplier * PAGE_DIMENSIONS.HEIGHT;
          parallaxTransform = `translate(${offsetX}px, ${offsetY}px)`;
        }

        // Si blur > 0, on wrappe l'image dans un conteneur avec overflow:visible
        // pour éviter que le flou soit tronqué
        if (blurAmount > 0) {
          const blurCompensation = blurAmount * 3; // Marge pour le débordement du blur
          // translateZ maintient le stacking context sur iOS avec transforms
          const zOffset = `translateZ(${zIndex * 0.1}px)`;
          return (
            <div
              key={element.id}
              {...elementProps}
              style={{
                ...baseStyle,
                overflow: 'visible',
                filter: undefined, // Le filter sera sur l'img, pas le wrapper
                transform: parallaxTransform
                  ? `${baseStyle.transform || ''} ${parallaxTransform} ${zOffset}`.trim()
                  : `${baseStyle.transform || ''} ${zOffset}`.trim(),
                transformStyle: 'preserve-3d',
                transition: img.parallaxEnabled ? 'transform 0.1s ease-out' : undefined,
              }}
            >
              {outlineSvg}
              {(element as any).colorOverlay && (
                <svg style={{ position: 'absolute', width: 0, height: 0, pointerEvents: 'none' }}>
                  <defs>
                    <filter id={`color-overlay-fr-${element.id}`} x="0" y="0" width="100%" height="100%">
                      <feFlood floodColor={(element as any).colorOverlay.color} floodOpacity={(element as any).colorOverlay.opacity} result="color" />
                      <feBlend in="color" in2="SourceGraphic" mode={(element as any).colorOverlay.blendMode || 'multiply'} result="blend" />
                      <feComposite in="blend" in2="SourceAlpha" operator="in" />
                    </filter>
                  </defs>
                </svg>
              )}
              <img
                src={getPublicStorageUrl(img.src)}
                alt={img.alt || 'Story image'}
                style={{
                  position: 'absolute',
                  top: -blurCompensation,
                  left: -blurCompensation,
                  width: `calc(100% + ${blurCompensation * 2}px)`,
                  height: `calc(100% + ${blurCompensation * 2}px)`,
                  objectFit: img.fit || 'cover',
                  opacity: img.filters?.opacity !== undefined ? img.filters.opacity : 1,
                  filter: filterStyle,
                  ...getBorderEffectStyle(img.borderEffect || 'none')
                }}
              />
            </div>
          );
        }

        // translateZ maintient le stacking context sur iOS avec transforms
        const zOffset = img.parallaxEnabled ? `translateZ(${zIndex * 0.1}px)` : '';

        return (
          <div
            key={element.id}
            {...elementProps}
            style={{
              ...baseStyle,
              overflow: outlineFilter ? 'visible' : 'hidden',
              transform: parallaxTransform
                ? `${baseStyle.transform || ''} ${parallaxTransform} ${zOffset}`.trim()
                : baseStyle.transform,
              transformStyle: img.parallaxEnabled ? 'preserve-3d' : undefined,
              transition: img.parallaxEnabled ? 'transform 0.1s ease-out' : undefined,
            }}
          >
            {outlineSvg}
            <img
              src={getPublicStorageUrl(img.src)}
              alt={img.alt || 'Story image'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: img.fit || 'cover',
                opacity: img.filters?.opacity !== undefined ? img.filters.opacity : 1,
                filter: filterStyle || undefined,
                ...getBorderEffectStyle(img.borderEffect || 'none')
              }}
            />
          </div>
        );
      }

      case 'shape': {
        const shape = element;
        const { style: shapeStyle, shapeType } = shape;

        let background = shapeStyle.fillColor;
        if (shapeStyle.gradient && shapeStyle.gradient.type !== 'none') {
          const { gradient } = shapeStyle;
          if (gradient.type === 'linear') {
            const cssDir = (gradient.direction || 'to-bottom').replace(/-/g, ' ');
            background = `linear-gradient(${cssDir}, ${gradient.startColor || '#3b82f6'}, ${gradient.endColor || '#ffffff'})`;
          } else if (gradient.type === 'radial') {
            background = `radial-gradient(circle, ${gradient.startColor || '#3b82f6'}, ${gradient.endColor || '#ffffff'})`;
          }
        }

        let clipPath = '';
        switch (shapeType) {
          case 'circle':
            clipPath = 'circle(50%)';
            break;
          case 'triangle':
            clipPath = 'polygon(50% 0%, 0% 100%, 100% 100%)';
            break;
          case 'star':
            clipPath = 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)';
            break;
          case 'arrow':
            clipPath = 'polygon(0% 40%, 60% 40%, 60% 0%, 100% 50%, 60% 100%, 60% 60%, 0% 60%)';
            break;
          case 'freeform':
            if (shape.points) {
              const pts = shape.points as any[];
              if (!pts.some((p: any) => p.curve)) {
                clipPath = `polygon(${pts.map((p: any) => `${p.x}% ${p.y}%`).join(', ')})`;
              } else {
                const w = size.width, h = size.height;
                let d = `M ${(pts[0].x * w / 100).toFixed(1)} ${(pts[0].y * h / 100).toFixed(1)}`;
                for (let i = 1; i < pts.length; i++) {
                  const p = pts[i];
                  if (p.curve) d += ` Q ${(p.curve.x * w / 100).toFixed(1)} ${(p.curve.y * h / 100).toFixed(1)} ${(p.x * w / 100).toFixed(1)} ${(p.y * h / 100).toFixed(1)}`;
                  else d += ` L ${(p.x * w / 100).toFixed(1)} ${(p.y * h / 100).toFixed(1)}`;
                }
                const cc = (pts[0] as any).closingCurve;
                if (cc) d += ` Q ${(cc.x * w / 100).toFixed(1)} ${(cc.y * h / 100).toFixed(1)} ${(pts[0].x * w / 100).toFixed(1)} ${(pts[0].y * h / 100).toFixed(1)}`;
                else d += ' Z';
                clipPath = `path('${d}')`;
              }
            }
            break;
          default:
            break;
        }

        const shapeStyles: React.CSSProperties = shapeStyle.textureUrl
          ? {
              backgroundImage: `url(${shapeStyle.textureUrl})`,
              backgroundRepeat: 'round',
            }
          : {
              background: background,
            };

        // SVG border path pour formes avec clip-path
        let borderPathD = '';
        if (clipPath && (shapeStyle.borderWidth || 0) > 0 && shapeStyle.borderColor !== 'transparent') {
          const w = size.width, h = size.height;
          switch (shapeType) {
            case 'triangle': borderPathD = `M ${w/2} 0 L 0 ${h} L ${w} ${h} Z`; break;
            case 'star': borderPathD = `M ${w*0.5} 0 L ${w*0.61} ${h*0.35} L ${w*0.98} ${h*0.35} L ${w*0.68} ${h*0.57} L ${w*0.79} ${h*0.91} L ${w*0.5} ${h*0.7} L ${w*0.21} ${h*0.91} L ${w*0.32} ${h*0.57} L ${w*0.02} ${h*0.35} L ${w*0.39} ${h*0.35} Z`; break;
            case 'arrow': borderPathD = `M 0 ${h*0.4} L ${w*0.6} ${h*0.4} L ${w*0.6} 0 L ${w} ${h*0.5} L ${w*0.6} ${h} L ${w*0.6} ${h*0.6} L 0 ${h*0.6} Z`; break;
            case 'freeform':
              if (shape.points && shape.points.length > 0) {
                const pts = shape.points as any[];
                borderPathD = `M ${(pts[0].x * w / 100).toFixed(1)} ${(pts[0].y * h / 100).toFixed(1)}`;
                for (let i = 1; i < pts.length; i++) {
                  const p = pts[i];
                  if (p.curve) borderPathD += ` Q ${(p.curve.x * w / 100).toFixed(1)} ${(p.curve.y * h / 100).toFixed(1)} ${(p.x * w / 100).toFixed(1)} ${(p.y * h / 100).toFixed(1)}`;
                  else borderPathD += ` L ${(p.x * w / 100).toFixed(1)} ${(p.y * h / 100).toFixed(1)}`;
                }
                const cc = (pts[0] as any).closingCurve;
                if (cc) borderPathD += ` Q ${(cc.x * w / 100).toFixed(1)} ${(cc.y * h / 100).toFixed(1)} ${(pts[0].x * w / 100).toFixed(1)} ${(pts[0].y * h / 100).toFixed(1)}`;
                else borderPathD += ' Z';
              }
              break;
          }
        }

        return (
          <div
            key={element.id}
            {...elementProps}
            style={{
              ...baseStyle,
              filter: [baseStyle.filter, shapeStyle.blur ? `blur(${shapeStyle.blur}px)` : ''].filter(Boolean).join(' ') || undefined,
              overflow: (shapeStyle.blur || element.dropShadow) ? 'visible' : undefined,
            }}
          >
            {(() => {
              const ds = element.dropShadow;
              let shadowFilter: string | undefined;
              if (ds) {
                const r = parseInt(ds.color.slice(1, 3), 16) || 0;
                const g = parseInt(ds.color.slice(3, 5), 16) || 0;
                const b = parseInt(ds.color.slice(5, 7), 16) || 0;
                shadowFilter = `drop-shadow(${ds.offsetX}px ${ds.offsetY}px ${ds.blur}px rgba(${r},${g},${b},${ds.opacity}))`;
              }
              const innerDiv = (
                <div style={{
                  width: '100%', height: '100%',
                  ...shapeStyles,
                  opacity: shapeStyle.opacity !== undefined ? shapeStyle.opacity : 1,
                  border: 'none',
                  borderRadius: shapeType === 'circle' ? '50%' : (shapeStyle.borderRadius ? `${shapeStyle.borderRadius}px` : undefined),
                  clipPath: clipPath || undefined,
                }} />
              );
              return shadowFilter ? <div style={{ width: '100%', height: '100%', filter: shadowFilter }}>{innerDiv}</div> : innerDiv;
            })()}
            {borderPathD && (
              <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}>
                <path d={borderPathD} fill="none" stroke={shapeStyle.borderColor} strokeWidth={shapeStyle.borderWidth} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        );
      }

      case 'choice-button': {
        const btn = element;
        const { style, content } = btn;

        return (
          <div
            key={element.id}
            {...elementProps}
            style={{
              ...baseStyle,
              backgroundColor: style.backgroundColor,
              color: style.color,
              border: style.borderWidth ? `${style.borderWidth}px solid ${style.borderColor}` : undefined,
              borderRadius: `${style.borderRadius || 12}px`,
              padding: `${style.padding || 12}px`,
              fontSize: `${style.fontSize}px`,
              fontFamily: style.fontFamily,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'default',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <span style={{ fontSize: '20px', marginRight: '8px' }}>👆</span>
            <span>{content[language] || content.fr}</span>
          </div>
        );
      }

      case 'drop-zone': {
        const dropZone = element;
        const { style } = dropZone;

        return (
          <div
            key={element.id}
            {...elementProps}
            style={{
              ...baseStyle,
              backgroundColor: style.fillColor,
              border: `${style.borderWidth}px ${style.borderStyle} ${style.borderColor}`,
              borderRadius: style.borderRadius ? `${style.borderRadius}px` : undefined,
              opacity: style.opacity,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
              fontSize: '12px',
            }}
          >
            {style.opacity > 0.1 && '📦 Drop Zone'}
          </div>
        );
      }

      case 'word-guess': {
        const wordGuess = element;
        const { style, word, placeholder, revealedIndices } = wordGuess;

        const wordText = word[language] || word.fr || '';
        const displayWord = wordText.split('').map((char: string, i: number) =>
          revealedIndices?.includes(i) ? char : placeholder || '_'
        ).join(' ');

        return (
          <div
            key={element.id}
            {...elementProps}
            style={{
              ...baseStyle,
              fontSize: `${style.fontSize}px`,
              fontFamily: style.fontFamily,
              color: style.color,
              letterSpacing: `${style.letterSpacing}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {displayWord}
          </div>
        );
      }

      case 'particle': {
        const particleEl = element as any;
        const pConfig = particleEl.config;
        if (!pConfig) return null;
        return (
          <div
            key={element.id}
            {...elementProps}
            style={{
              ...baseStyle,
              overflow: 'visible',
              pointerEvents: 'none',
            }}
          >
            <ParticleCanvas
              config={pConfig}
              width={element.size.width}
              height={element.size.height}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        );
      }

      default:
        return null;
    }
  };

  if (!currentPage) {
    return (
      <div className="h-screen-adaptive flex items-center justify-center bg-red-100">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-800 mb-4">Page introuvable: {currentPageKey}</h2>
          <p className="text-gray-600 mb-4">Pages disponibles: {Object.keys(storyPages).join(', ')}</p>
          <button onClick={handleBack} className="px-4 py-2 bg-blue-500 text-white rounded">
            Retour
          </button>
        </div>
      </div>
    );
  }

  const pageElements = currentPage.content?.elements || [];
  const allPageChoices = currentPage.choices || [];

  // For first-time reading, check which choices are accessible
  const hasChosenBefore = !isReplay && allPageChoices.some((choice: any) =>
    allVisitedPages.includes(choice.nextPage)
  );

  // Keep all choices visible, but mark some as disabled
  const pageChoices = allPageChoices;

  // Ending detection based on ORIGINAL choices, not filtered ones
  const isEnding = allPageChoices.length === 0 && !currentPage.nextPage;

  const navigateToPage = (nextPageKey: string) => {
    // Reset carousel refs on page change
    autoTextCarouselRefs.current = {};

    // Stop all sounds when changing page
    stopAllEventSounds();

    const newHistory = [...pageHistory, nextPageKey];

    // Add to all visited pages if not already there
    const newAllVisited = allVisitedPages.includes(nextPageKey)
      ? allVisitedPages
      : [...allVisitedPages, nextPageKey];

    setCurrentPageKey(nextPageKey);
    setPageHistory(newHistory);
    setAllVisitedPages(newAllVisited);
    setShowChoices(false);
    setElementPositions({}); // Reset element positions on page change
    setUserZoom(1); // Reset zoom on page change
    setZoomOffset({ x: 0, y: 0 });

    // Save progress ONLY if this is the furthest point reached (only for first-time reading)
    if (!isReplay) {
      const savedHistoryLength = savedProgress?.pageHistory.length || 0;
      const isFurthest = newHistory.length > savedHistoryLength;

      if (isFurthest) {
        onSaveProgress({
          storyId: selectedBook.id,
          currentPageKey: nextPageKey,
          pageHistory: newHistory,
          allVisitedPages: newAllVisited,
          startedAt: savedProgress?.startedAt || new Date().toISOString()
        });
      }
    }
  };

  const handleNext = () => {
    // Si les choix sont affichés, ne rien faire
    if (showChoices) {
      return;
    }

    // D'abord, avancer les carousels de texte auto
    const carouselRefs = autoTextCarouselRefs.current;
    for (const id of Object.keys(carouselRefs)) {
      const ref = carouselRefs[id];
      if (ref && !ref.isAtEnd()) {
        ref.advanceCarousel();
        return; // On avance le carousel, on ne change pas de page
      }
    }

    if (pageChoices.length > 0) {
      setShowChoices(true);
      return;
    }

    if (currentPage.nextPage) {
      navigateToPage(currentPage.nextPage);
      return;
    }

    if (isEnding) {
      // Clear progress when book is completed
      onClearProgress(selectedBook.id);

      // Collect rewards only for first-time reading
      const visitedRewardIds: number[] = [];
      if (!isReplay) {
        pageHistory.forEach(pageKey => {
          const page = storyPages[pageKey];
          if (page?.reward && page.reward.id) {
            visitedRewardIds.push(page.reward.id);
          }
        });

        console.log('🎯 Pages visitées:', pageHistory);
        console.log('🎁 Récompenses des pages visitées:', visitedRewardIds);
      } else {
        console.log('🔄 Relecture - Aucune récompense donnée');
      }

      onBookComplete(selectedBook, visitedRewardIds);
    }
  };

  const handleChoiceClick = (nextPageKey: string) => {
    navigateToPage(nextPageKey);
  };

  const handlePrevious = () => {
    // Si les choix sont affichés, ne rien faire
    if (showChoices) {
      return;
    }

    if (pageHistory.length <= 1) return;

    // Stop all sounds when changing page
    stopAllEventSounds();

    const newHistory = [...pageHistory];
    newHistory.pop();
    const previousPage = newHistory[newHistory.length - 1];

    setPageHistory(newHistory);
    setCurrentPageKey(previousPage);
    setShowChoices(false);
    setUserZoom(1); // Reset zoom on page change
    setZoomOffset({ x: 0, y: 0 });
  };

  // Pinch-to-zoom and pan handlers
  const getDistance = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getPinchCenter = (touches: React.TouchList) => {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2
    };
  };

  const handlePinchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault(); // Prevent native browser zoom
      initialPinchDistance.current = getDistance(e.touches);
      initialZoom.current = userZoom;
      pinchCenter.current = getPinchCenter(e.touches);
      if (zoomContainerRef.current) {
        initialScrollPos.current = {
          x: zoomContainerRef.current.scrollLeft,
          y: zoomContainerRef.current.scrollTop
        };
      }
    }
  };

  const handlePinchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && initialPinchDistance.current !== null) {
      e.preventDefault();

      const currentDistance = getDistance(e.touches);
      const currentCenter = getPinchCenter(e.touches);
      const scale = currentDistance / initialPinchDistance.current;
      const newZoom = Math.min(Math.max(initialZoom.current * scale, 1), 3);

      // Calculate pan delta (movement of the pinch center)
      const panDeltaX = pinchCenter.current.x - currentCenter.x;
      const panDeltaY = pinchCenter.current.y - currentCenter.y;

      setUserZoom(newZoom);

      // Apply both zoom compensation and pan
      if (zoomContainerRef.current) {
        const container = zoomContainerRef.current;
        const rect = container.getBoundingClientRect();
        const zoomRatio = newZoom / initialZoom.current;

        // Position of initial pinch center relative to scroll viewport
        const viewportX = pinchCenter.current.x - rect.left;
        const viewportY = pinchCenter.current.y - rect.top;

        // Content position at initial pinch center
        const contentX = initialScrollPos.current.x + viewportX;
        const contentY = initialScrollPos.current.y + viewportY;

        // After zoom, this content point should still be under the current finger position
        // New scroll = content position after zoom - current viewport position
        const newScrollX = (contentX * zoomRatio) - viewportX + panDeltaX;
        const newScrollY = (contentY * zoomRatio) - viewportY + panDeltaY;

        container.scrollLeft = Math.max(0, newScrollX);
        container.scrollTop = Math.max(0, newScrollY);
      }
    }
  };

  const handlePinchEnd = () => {
    initialPinchDistance.current = null;
  };

  const resetZoom = () => {
    setUserZoom(1);
    setZoomOffset({ x: 0, y: 0 });
    if (zoomContainerRef.current) {
      zoomContainerRef.current.scrollLeft = 0;
      zoomContainerRef.current.scrollTop = 0;
    }
  };

  // Native mobile layout: full screen with side arrows
  if (isNative) {
    return (
      <div
        className="fixed inset-0 flex items-center justify-center transition-colors duration-500 safe-area-top safe-area-bottom"
        style={{ background: pageBackgroundColor, touchAction: 'manipulation' }}
      >
        {/* Main row: left controls | canvas | right controls */}
        <div className="flex items-stretch">
          {/* Left column: X button on top, arrow centered, reset zoom on bottom */}
          <div className="flex flex-col items-center justify-between w-8 py-1">
            {/* X button - top (with small top margin) */}
            <button
              onClick={handleBack}
              className={`p-1 mt-2 ${controlColor} ${controlColorActive}`}
            >
              <X size={20} />
            </button>

            {/* Left arrow - middle */}
            <button
              onClick={handlePrevious}
              disabled={pageHistory.length <= 1}
              className={`z-40 flex items-center justify-center disabled:opacity-30 transition-colors ${controlColor}`}
            >
              <ChevronLeft size={24} />
            </button>

            {/* Reset zoom button - bottom (only shown when zoomed, with bottom margin) */}
            {userZoom > 1 ? (
              <button
                onClick={resetZoom}
                className={`p-1 mb-2 ${controlColor} ${controlColorActive}`}
                aria-label="Réinitialiser le zoom"
              >
                <Minimize size={18} />
              </button>
            ) : (
              <div className="h-7" /> // Spacer to maintain layout
            )}
          </div>

          {/* Canvas column with progress bar */}
          <div className="flex flex-col">
            {/* Canvas with pinch-to-zoom */}
            <div
              ref={zoomContainerRef}
              className={`${userZoom > 1 ? 'overflow-auto' : 'overflow-hidden'}`}
              style={{
                maxWidth: `${PAGE_DIMENSIONS.WIDTH * canvasScale}px`,
                maxHeight: `${PAGE_DIMENSIONS.HEIGHT * canvasScale}px`,
              }}
            >
              {/* Inner wrapper to hold scaled content */}
              <div
                style={{
                  width: `${PAGE_DIMENSIONS.WIDTH * canvasScale * userZoom}px`,
                  height: `${PAGE_DIMENSIONS.HEIGHT * canvasScale * userZoom}px`,
                  flexShrink: 0,
                }}
              >
                <div
                  ref={contentRef}
                  className="bg-white rounded-lg shadow-lg relative overflow-visible"
                  style={{
                    width: `${PAGE_DIMENSIONS.WIDTH}px`,
                    height: `${PAGE_DIMENSIONS.HEIGHT}px`,
                    transform: `scale(${canvasScale * userZoom})`,
                    transformOrigin: 'top left',
                    backgroundColor: currentPage.content?.elements?.some((el: any) => el.type === 'background')
                      ? 'transparent'
                      : (currentPage.content?.backgroundColor || '#ffffff'),
                    // Prevent iOS text/image selection
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                    WebkitTouchCallout: 'none'
                  }}
                >
                  {/* Loading overlay - masque le contenu pendant le préchargement */}
                  {!pageReady && (
                    <div
                      className="absolute inset-0 z-[9999] flex items-center justify-center"
                      style={{ backgroundColor: pageBackgroundColor }}
                    >
                      <div className="animate-pulse">
                        <div className="w-16 h-16 border-4 border-gray-300 border-t-pink-500 rounded-full animate-spin" />
                      </div>
                    </div>
                  )}

                  <div style={{
                    opacity: pageReady ? 1 : 0,
                    transition: 'opacity 0.3s ease-in',
                  }}>
                  {pageElements
                    .slice()
                    .sort((a: any, b: any) => a.zIndex - b.zIndex)
                    .map((element: any) => renderElement(element))}
                  </div>

                  {/* Choices overlay */}
                  {pageChoices.length > 0 && showChoices && (
                    <div
                      className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]"
                      onClick={() => setShowChoices(false)}
                    >
                      <div
                        className="bg-white rounded-3xl p-14 max-w-3xl w-full mx-10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <h3 className="text-5xl font-semibold text-gray-700 mb-10 text-center">
                          Que choisis-tu ?
                        </h3>
                        <div className="space-y-6">
                          {pageChoices.map((choice: any, index: number) => {
                            const isAccessible = isReplay || !hasChosenBefore || allVisitedPages.includes(choice.nextPage);

                            return (
                              <button
                                key={index}
                                onClick={() => isAccessible && handleChoiceClick(choice.nextPage)}
                                disabled={!isAccessible}
                                className={`flex items-center justify-center gap-5 p-8 rounded-2xl font-semibold shadow-lg w-full transition-all text-2xl ${
                                  isAccessible
                                    ? 'bg-gradient-to-r from-blue-400 to-purple-500 text-white active:scale-95'
                                    : 'bg-gray-300 text-gray-500 opacity-60'
                                }`}
                              >
                                <span className="text-5xl">{isAccessible ? '👆' : '🔒'}</span>
                                <span>{choice.text[language] || choice.text.fr}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Ending indicator */}
                  {isEnding && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-50">
                      <div className="text-xs text-white bg-green-500 px-3 py-1 rounded-full shadow">
                        Fin 🎉
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Progress bar - below canvas */}
            <div className="pt-1">
              <div className="w-full bg-black/20 rounded-full h-1">
                <div
                  className="bg-white h-1 rounded-full transition-all duration-500 shadow-sm"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </div>

          {/* Right column: pagination on top, arrow centered, mute on bottom */}
          <div className="flex flex-col items-center justify-between w-8 py-1">
            {/* Pagination - top (with small top margin) */}
            <div className={`text-xs mt-2 ${controlColor}`}>
              {pagesVisited}
            </div>

            {/* Right arrow - middle */}
            <button
              onClick={handleNext}
              className={`z-40 flex items-center justify-center transition-colors ${controlColor}`}
            >
              <ChevronRight size={24} />
            </button>

            {/* Mute button - bottom (only if audio available) */}
            {currentPage?.content?.audio?.backgroundMusic?.url ? (
              <button
                onClick={() => setIsMuted(!isMuted)}
                className={`p-1 ${controlColor} ${controlColorActive}`}
              >
                {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
            ) : (
              <div className="h-7" /> // Spacer to maintain layout
            )}
          </div>
        </div>

        {/* Toast message */}
        {toastMessage && (
          <div className="fixed bottom-12 left-1/2 transform -translate-x-1/2 z-[20000]">
            <div className="bg-gray-900 bg-opacity-90 text-white px-4 py-2 rounded-lg shadow-xl flex items-center gap-2 text-sm">
              <span>{toastMessage}</span>
              <button
                onClick={() => setToastMessage(null)}
                className="ml-1 text-gray-400"
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Web layout - inspired by native with side arrows
  return (
    <div
      className="h-screen-adaptive flex flex-col transition-colors duration-500"
      style={{ background: pageBackgroundColor }}
    >
      {/* Compact Header */}
      <div className="flex justify-between items-center px-4 py-2">
        <button
          onClick={handleBack}
          className={`p-2 rounded-full transition-colors ${
            isBackgroundDark
              ? 'text-white/80 hover:text-white hover:bg-white/10'
              : 'text-gray-600 hover:text-gray-800 hover:bg-black/5'
          }`}
        >
          <X size={24} />
        </button>

        <h2 className={`text-sm font-medium font-title truncate max-w-md ${
          isBackgroundDark ? 'text-white/90' : 'text-gray-700'
        }`}>
          {selectedBook.fullData?.translations?.[language]?.title || selectedBook.fullData?.translations?.fr?.title || selectedBook.title}
        </h2>

        <div className="flex items-center gap-2">
          {currentPage?.content?.audio?.backgroundMusic?.url && (
            <button
              onClick={() => setIsMuted(!isMuted)}
              className={`p-2 rounded-full transition-colors ${
                isBackgroundDark
                  ? 'text-white/80 hover:text-white hover:bg-white/10'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-black/5'
              }`}
            >
              {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
          )}
          <div className={`text-xs px-2 py-1 rounded-full ${
            isBackgroundDark ? 'bg-white/20 text-white' : 'bg-black/10 text-gray-600'
          }`}>
            {pagesVisited}
          </div>
        </div>
      </div>

      {/* Main content area with side arrows */}
      <div className="flex-1 flex items-center justify-center px-2">
        {/* Left arrow */}
        <button
          onClick={handlePrevious}
          disabled={pageHistory.length <= 1}
          className={`flex-shrink-0 w-12 h-12 md:w-16 md:h-16 flex items-center justify-center rounded-full transition-all disabled:opacity-20 ${
            isBackgroundDark
              ? 'text-white/80 hover:text-white hover:bg-white/10'
              : 'text-gray-500 hover:text-gray-700 hover:bg-black/5'
          }`}
        >
          <ChevronLeft size={32} className="md:w-10 md:h-10" />
        </button>

        {/* Canvas container */}
        <div className="flex flex-col items-center mx-2 md:mx-4">
          <div
            className="relative overflow-hidden"
            style={{
              width: `${PAGE_DIMENSIONS.WIDTH * canvasScale}px`,
              height: `${PAGE_DIMENSIONS.HEIGHT * canvasScale}px`,
            }}
          >
            <div
              ref={contentRef}
              className="bg-white rounded-lg shadow-2xl relative overflow-visible"
              style={{
                width: `${PAGE_DIMENSIONS.WIDTH}px`,
                height: `${PAGE_DIMENSIONS.HEIGHT}px`,
                transform: `scale(${canvasScale})`,
                transformOrigin: 'top left',
                backgroundColor: currentPage.content?.elements?.some((el: any) => el.type === 'background')
                  ? 'transparent'
                  : (currentPage.content?.backgroundColor || '#ffffff')
              }}
            >
              {/* Loading overlay - masque le contenu pendant le préchargement */}
              {!pageReady && (
                <div
                  className="absolute inset-0 z-[9999] flex items-center justify-center"
                  style={{ backgroundColor: pageBackgroundColor }}
                >
                  <div className="animate-pulse">
                    <div className="w-16 h-16 border-4 border-gray-300 border-t-pink-500 rounded-full animate-spin" />
                  </div>
                </div>
              )}

              <div style={{
                opacity: pageReady ? 1 : 0,
                transition: 'opacity 0.3s ease-in',
              }}>
              {pageElements
                .slice()
                .sort((a: any, b: any) => a.zIndex - b.zIndex)
                .map((element: any) => renderElement(element))}
              </div>

              {/* Ending indicator */}
              {isEnding && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-50">
                  <div className="text-sm text-white bg-green-500 px-4 py-2 rounded-full shadow-lg">
                    Fin de l'histoire 🎉
                  </div>
                </div>
              )}

              {/* Choices overlay - dans le canvas */}
              {pageChoices.length > 0 && showChoices && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
                  <div
                    className="bg-white rounded-[2rem] p-14 max-w-3xl w-full mx-10"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h3 className="text-4xl font-semibold text-gray-700 mb-8 text-center">
                      Que choisis-tu ?
                    </h3>
                    <div className="space-y-5">
                      {pageChoices.map((choice: any, index: number) => {
                        const isAccessible = isReplay || !hasChosenBefore || allVisitedPages.includes(choice.nextPage);

                        return (
                          <button
                            key={index}
                            onClick={() => isAccessible && handleChoiceClick(choice.nextPage)}
                            disabled={!isAccessible}
                            className={`flex items-center justify-center gap-5 p-8 rounded-2xl font-semibold shadow-lg w-full transition-all text-3xl ${
                              isAccessible
                                ? 'bg-gradient-to-r from-blue-400 to-purple-500 text-white hover:from-blue-500 hover:to-purple-600 hover:scale-105 cursor-pointer'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                            }`}
                          >
                            <span className="text-5xl">{isAccessible ? '👆' : '🔒'}</span>
                            <span>{choice.text[language] || choice.text.fr}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Progress bar under canvas */}
          <div className="w-full mt-3">
            <div className={`w-full rounded-full h-1.5 ${
              isBackgroundDark ? 'bg-white/20' : 'bg-black/10'
            }`}>
              <div
                className="bg-gradient-to-r from-green-400 to-blue-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        </div>

        {/* Right arrow */}
        <button
          onClick={handleNext}
          className={`flex-shrink-0 w-12 h-12 md:w-16 md:h-16 flex items-center justify-center rounded-full transition-all ${
            isBackgroundDark
              ? 'text-white/80 hover:text-white hover:bg-white/10'
              : 'text-gray-500 hover:text-gray-700 hover:bg-black/5'
          }`}
        >
          <ChevronRight size={32} className="md:w-10 md:h-10" />
        </button>
      </div>

      {/* Bottom hint for choices/ending - invisible quand choix affichés pour garder l'espace */}
      {pageChoices.length > 0 && (
        <div className={`text-center pb-4 ${showChoices ? 'invisible' : ''}`}>
          <span className={`text-sm ${isBackgroundDark ? 'text-white/60' : 'text-gray-500'}`}>
            Cliquez sur la flèche pour voir les choix →
          </span>
        </div>
      )}

      {/* Toast message */}
      {toastMessage && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-[20000]">
          <div className="bg-gray-900 bg-opacity-90 text-white px-6 py-3 rounded-lg shadow-xl flex items-center gap-3">
            <span>{toastMessage}</span>
            <button
              onClick={() => setToastMessage(null)}
              className="ml-2 text-gray-400 hover:text-white"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernReadingScreen;
